import type {
    ApprovalInboxDto,
    AssignMilestoneStageDto,
    AssignCostEntryDto,
    CostEntryKind,
    CreateProjectChangeRequestDto,
    CreateProjectCheckpointReportDto,
    CreateProjectIssueDto,
    CreateProjectLessonDto,
    CreateProjectMeasurementDto,
    CreateProjectRiskDto,
    CostImportResultDto,
    CreateGovernedMilestoneDto,
    DecideProjectChangeRequestDto,
    DecideProjectGovernanceDto,
    ProjectChangeRequestDto,
    ProjectCheckpointReportDto,
    ProjectControlsDto,
    ProjectCostEntryDto,
    ProjectGovernanceDto,
    ProjectIssueDto,
    ProjectLessonDto,
    ProjectMeasurementDto,
    ProjectRegistersDto,
    ProjectRiskDto,
    RaiseExceptionReportDto,
    RecordBoardOpinionDto,
    SetBaselineBudgetDto,
    SetChangeAuthorityDto,
    SetCommunicationPlanDto,
    SetCostEntryStageDto,
    SetMilestoneForecastDto,
    SetMilestoneDurationDto,
    SetMilestonePriorityDto,
    SetStageEstimateDto,
    SetProjectDeliveryModeDto,
    SetProjectGovernanceFlagsDto,
    SetProjectTypeDto,
    SubmitProjectGovernanceDto,
    UpdateGovernedMilestoneDto,
    UpdateProjectGovernanceRolesDto,
    UpsertProjectStagesDto,
    UpdateProjectIssueDto,
    UpdateProjectRiskDto,
    UpsertProjectTolerancesDto,
    type ProjectScheduleDto,
    type ProjectMilestoneDependencyDto,
    type SetProjectMilestoneDependenciesDto,
} from "@paladin-monorepo/common";
import API from "../api";

const base = (projectId: string) => `/projects/${encodeURIComponent(projectId)}/governance`;

export const scheduleQueryKey = (projectId: string) => ['project-schedule', projectId] as const;
export const milestoneDependenciesQueryKey = (projectId: string) =>
    ['project-milestone-dependencies', projectId] as const;

/** Board actions send a new key per call, so a network retry of the same call runs once. */
const idempotent = () => ({ headers: { "Idempotency-Key": crypto.randomUUID() } });

export const projectGovernanceApi = {
    /** Returns null when governance is not open for the project. */
    async get(projectId: string): Promise<ProjectGovernanceDto | null> {
        const { data } = await API.get<ProjectGovernanceDto | "">(base(projectId));
        return data || null;
    },
    async open(projectId: string): Promise<ProjectGovernanceDto> {
        return API.post<ProjectGovernanceDto>(base(projectId)).then((res) => res.data);
    },
    async assignRoles(projectId: string, dto: UpdateProjectGovernanceRolesDto): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(`${base(projectId)}/roles`, dto).then((res) => res.data);
    },
    async setDeliveryMode(projectId: string, dto: SetProjectDeliveryModeDto): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(`${base(projectId)}/delivery-mode`, dto).then((res) => res.data);
    },
    async upsertStages(projectId: string, dto: UpsertProjectStagesDto): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(`${base(projectId)}/stages`, dto).then((res) => res.data);
    },
    async createMilestone(projectId: string, dto: CreateGovernedMilestoneDto): Promise<ProjectGovernanceDto> {
        return API.post<ProjectGovernanceDto>(`${base(projectId)}/milestones`, dto).then((res) => res.data);
    },
    async updateMilestone(
        projectId: string,
        milestoneId: string,
        dto: UpdateGovernedMilestoneDto,
    ): Promise<ProjectGovernanceDto> {
        return API.patch<ProjectGovernanceDto>(
            `${base(projectId)}/milestones/${encodeURIComponent(milestoneId)}`,
            dto,
        ).then((res) => res.data);
    },
    async deleteMilestone(projectId: string, milestoneId: string, expectedVersion: number): Promise<ProjectGovernanceDto> {
        return API.delete<ProjectGovernanceDto>(`${base(projectId)}/milestones/${encodeURIComponent(milestoneId)}`, {
            params: { expectedVersion },
        }).then((res) => res.data);
    },
    async assignMilestoneStage(
        projectId: string,
        milestoneId: string,
        dto: AssignMilestoneStageDto,
    ): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(
            `${base(projectId)}/milestones/${encodeURIComponent(milestoneId)}/stage`,
            dto,
        ).then((res) => res.data);
    },
    async submit(projectId: string, dto: SubmitProjectGovernanceDto): Promise<ProjectGovernanceDto> {
        return API.post<ProjectGovernanceDto>(`${base(projectId)}/submit`, dto, idempotent()).then((res) => res.data);
    },
    async draftSubmitSummary(
        projectId: string,
        dto: Pick<SubmitProjectGovernanceDto, 'kind'>,
    ): Promise<{ draft: string | null }> {
        return API.post<{ draft: string | null }>(`${base(projectId)}/submit/summary-draft`, dto).then(
            (res) => res.data,
        );
    },
    async decide(projectId: string, dto: DecideProjectGovernanceDto): Promise<ProjectGovernanceDto> {
        return API.post<ProjectGovernanceDto>(`${base(projectId)}/decisions`, dto, idempotent()).then(
            (res) => res.data,
        );
    },
    async setFlags(projectId: string, dto: SetProjectGovernanceFlagsDto): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(`${base(projectId)}/flags`, dto).then((res) => res.data);
    },
    async setProjectType(projectId: string, dto: SetProjectTypeDto): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(`${base(projectId)}/project-type`, dto).then((res) => res.data);
    },
    async recordOpinion(
        projectId: string,
        requestId: string,
        dto: RecordBoardOpinionDto,
    ): Promise<ProjectGovernanceDto> {
        return API.post<ProjectGovernanceDto>(
            `${base(projectId)}/requests/${encodeURIComponent(requestId)}/opinions`,
            dto,
            idempotent(),
        ).then((res) => res.data);
    },
    async getSchedule(projectId: string): Promise<ProjectScheduleDto | null> {
        return API.get<ProjectScheduleDto | "">(`${base(projectId)}/schedule`).then((res) => res.data || null);
    },
    async listMilestoneDependencies(projectId: string): Promise<ProjectMilestoneDependencyDto[]> {
        return API.get<ProjectMilestoneDependencyDto[]>(`${base(projectId)}/milestone-dependencies`).then(
            (res) => res.data,
        );
    },
    async setMilestoneDependencies(
        projectId: string,
        dto: SetProjectMilestoneDependenciesDto,
    ): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(`${base(projectId)}/milestone-dependencies`, dto).then(
            (res) => res.data,
        );
    },
    async getControls(projectId: string): Promise<ProjectControlsDto> {
        return API.get<ProjectControlsDto>(`${base(projectId)}/controls`).then((res) => res.data);
    },
    async setBaselineBudget(projectId: string, dto: SetBaselineBudgetDto): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(`${base(projectId)}/baseline-budget`, dto).then((res) => res.data);
    },
    async upsertTolerances(projectId: string, dto: UpsertProjectTolerancesDto): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(`${base(projectId)}/tolerances`, dto).then((res) => res.data);
    },
    async setStageEstimate(projectId: string, stageId: string, dto: SetStageEstimateDto): Promise<ProjectControlsDto> {
        return API.put<ProjectControlsDto>(
            `${base(projectId)}/stages/${encodeURIComponent(stageId)}/estimate`,
            dto,
        ).then((res) => res.data);
    },
    async setMilestoneForecast(
        projectId: string,
        milestoneId: string,
        dto: SetMilestoneForecastDto,
    ): Promise<ProjectControlsDto> {
        return API.put<ProjectControlsDto>(
            `${base(projectId)}/milestones/${encodeURIComponent(milestoneId)}/forecast`,
            dto,
        ).then((res) => res.data);
    },
    async setMilestoneDuration(
        projectId: string,
        milestoneId: string,
        dto: SetMilestoneDurationDto,
    ): Promise<ProjectControlsDto> {
        return API.put<ProjectControlsDto>(
            `${base(projectId)}/milestones/${encodeURIComponent(milestoneId)}/duration`,
            dto,
        ).then((res) => res.data);
    },
    async setMilestonePriority(
        projectId: string,
        milestoneId: string,
        dto: SetMilestonePriorityDto,
    ): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(
            `${base(projectId)}/milestones/${encodeURIComponent(milestoneId)}/priority`,
            dto,
        ).then((res) => res.data);
    },
    async listCosts(projectId: string): Promise<ProjectCostEntryDto[]> {
        return API.get<ProjectCostEntryDto[]>(`${base(projectId)}/costs`).then((res) => res.data);
    },
    async setCostStage(projectId: string, entryId: string, dto: SetCostEntryStageDto): Promise<ProjectCostEntryDto> {
        return API.put<ProjectCostEntryDto>(
            `${base(projectId)}/costs/${encodeURIComponent(entryId)}/stage`,
            dto,
        ).then((res) => res.data);
    },
    async listRegisters(projectId: string): Promise<ProjectRegistersDto> {
        return API.get<ProjectRegistersDto>(`${base(projectId)}/registers`).then((res) => res.data);
    },
    async createRisk(projectId: string, dto: CreateProjectRiskDto): Promise<ProjectRiskDto> {
        return API.post<ProjectRiskDto>(`${base(projectId)}/risks`, dto, idempotent()).then((res) => res.data);
    },
    async updateRisk(projectId: string, riskId: string, dto: UpdateProjectRiskDto): Promise<ProjectRiskDto> {
        return API.patch<ProjectRiskDto>(
            `${base(projectId)}/risks/${encodeURIComponent(riskId)}`,
            dto,
            idempotent(),
        ).then((res) => res.data);
    },
    async acceptRiskProposal(projectId: string, riskId: string): Promise<ProjectRiskDto> {
        return API.post<ProjectRiskDto>(
            `${base(projectId)}/risks/${encodeURIComponent(riskId)}/accept`,
            {},
            idempotent(),
        ).then((res) => res.data);
    },
    async rejectRiskProposal(projectId: string, riskId: string): Promise<ProjectRiskDto> {
        return API.post<ProjectRiskDto>(
            `${base(projectId)}/risks/${encodeURIComponent(riskId)}/reject`,
            {},
            idempotent(),
        ).then((res) => res.data);
    },
    async createLesson(projectId: string, dto: CreateProjectLessonDto): Promise<ProjectLessonDto> {
        return API.post<ProjectLessonDto>(`${base(projectId)}/lessons`, dto, idempotent()).then((res) => res.data);
    },
    async acceptLessonProposal(projectId: string, lessonId: string): Promise<ProjectLessonDto> {
        return API.post<ProjectLessonDto>(
            `${base(projectId)}/lessons/${encodeURIComponent(lessonId)}/accept`,
            {},
            idempotent(),
        ).then((res) => res.data);
    },
    async rejectLessonProposal(projectId: string, lessonId: string): Promise<ProjectLessonDto> {
        return API.post<ProjectLessonDto>(
            `${base(projectId)}/lessons/${encodeURIComponent(lessonId)}/reject`,
            {},
            idempotent(),
        ).then((res) => res.data);
    },
    async approveRiskResponse(projectId: string, riskId: string): Promise<ProjectRiskDto> {
        return API.post<ProjectRiskDto>(
            `${base(projectId)}/risks/${encodeURIComponent(riskId)}/response-approval`,
            {},
            idempotent(),
        ).then((res) => res.data);
    },
    async createIssue(projectId: string, dto: CreateProjectIssueDto): Promise<ProjectIssueDto> {
        return API.post<ProjectIssueDto>(`${base(projectId)}/issues`, dto, idempotent()).then((res) => res.data);
    },
    async updateIssue(projectId: string, issueId: string, dto: UpdateProjectIssueDto): Promise<ProjectIssueDto> {
        return API.patch<ProjectIssueDto>(
            `${base(projectId)}/issues/${encodeURIComponent(issueId)}`,
            dto,
            idempotent(),
        ).then((res) => res.data);
    },
    async createMeasurement(projectId: string, dto: CreateProjectMeasurementDto): Promise<ProjectMeasurementDto> {
        return API.post<ProjectMeasurementDto>(`${base(projectId)}/measurements`, dto, idempotent()).then(
            (res) => res.data,
        );
    },
    async listCheckpoints(projectId: string, milestoneId: string): Promise<ProjectCheckpointReportDto[]> {
        return API.get<ProjectCheckpointReportDto[]>(
            `${base(projectId)}/milestones/${encodeURIComponent(milestoneId)}/checkpoints`,
        ).then((res) => res.data);
    },
    async createCheckpoint(
        projectId: string,
        milestoneId: string,
        dto: CreateProjectCheckpointReportDto,
    ): Promise<ProjectCheckpointReportDto> {
        return API.post<ProjectCheckpointReportDto>(
            `${base(projectId)}/milestones/${encodeURIComponent(milestoneId)}/checkpoints`,
            dto,
            idempotent(),
        ).then((res) => res.data);
    },
    async setChangeAuthority(projectId: string, dto: SetChangeAuthorityDto): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(`${base(projectId)}/change-authority`, dto).then((res) => res.data);
    },
    async setCommunicationPlan(projectId: string, dto: SetCommunicationPlanDto): Promise<ProjectGovernanceDto> {
        return API.put<ProjectGovernanceDto>(`${base(projectId)}/communication-plan`, dto).then((res) => res.data);
    },
    async raiseExceptionReport(projectId: string, dto: RaiseExceptionReportDto): Promise<ProjectGovernanceDto> {
        return API.post<ProjectGovernanceDto>(`${base(projectId)}/exception-reports`, dto, idempotent()).then(
            (res) => res.data,
        );
    },
    async listChangeRequests(projectId: string): Promise<ProjectChangeRequestDto[]> {
        return API.get<ProjectChangeRequestDto[]>(`${base(projectId)}/change-requests`).then((res) => res.data);
    },
    async createChangeRequest(
        projectId: string,
        dto: CreateProjectChangeRequestDto,
    ): Promise<ProjectChangeRequestDto> {
        return API.post<ProjectChangeRequestDto>(`${base(projectId)}/change-requests`, dto, idempotent()).then(
            (res) => res.data,
        );
    },
    async decideChangeRequest(
        projectId: string,
        id: string,
        dto: DecideProjectChangeRequestDto,
    ): Promise<ProjectChangeRequestDto> {
        return API.post<ProjectChangeRequestDto>(
            `${base(projectId)}/change-requests/${encodeURIComponent(id)}/decisions`,
            dto,
            idempotent(),
        ).then((res) => res.data);
    },
};

export const approvalsApi = {
    async inbox(): Promise<ApprovalInboxDto> {
        return API.get<ApprovalInboxDto>("/approvals/inbox").then((res) => res.data);
    },
};

export const costsApi = {
    async importCsv(file: File, kind?: CostEntryKind): Promise<CostImportResultDto> {
        const form = new FormData();
        form.append("file", file);
        if (kind) form.append("kind", kind);
        return API.post<CostImportResultDto>("/costs/import", form, idempotent()).then((res) => res.data);
    },
    async unmatched(): Promise<ProjectCostEntryDto[]> {
        return API.get<ProjectCostEntryDto[]>("/costs/unmatched").then((res) => res.data);
    },
    async assign(entryId: string, dto: AssignCostEntryDto): Promise<ProjectCostEntryDto> {
        return API.post<ProjectCostEntryDto>(`/costs/${encodeURIComponent(entryId)}/assign`, dto, idempotent()).then(
            (res) => res.data,
        );
    },
};
